import { create } from 'zustand';
import type { AnnouncementState } from './api';
import { LOAD_STATUS } from './constants';
import { hasReceipt } from './rules';
import type { LoadStatus, Receipt, Surface } from './types';

interface AnnouncementStore extends AnnouncementState {
  // Whose state this is; another user (a re-login) loads afresh.
  userId: string | null;
  status: LoadStatus;
  // The moment the rules look at; moved on every page, so scheduled posts appear and old ones expire without a reload.
  now: number;
  // The posts in the open popup and where it was opened from; null when closed.
  popup: { postIds: string[]; surface: Surface } | null;
  panelOpen: boolean;
  // A ?whats-new= link waiting for the screen to be free; kept across pages and layout remounts.
  linkedPostId: string | null;
  startLoad: (userId: string) => void;
  // null = the load failed: no posts this session.
  finishLoad: (userId: string, state: AnnouncementState | null) => void;
  addReceipts: (receipts: Receipt[]) => void;
  tick: () => void;
  openPopup: (postIds: string[], surface: Surface) => void;
  closePopup: () => void;
  setPanelOpen: (open: boolean) => void;
  setLinkedPostId: (postId: string | null) => void;
}

// Receipts already there win, so the first time a post was seen in a place is the one kept.
const mergeReceipts = (base: Receipt[], extra: Receipt[]): Receipt[] =>
  extra.reduce((all, r) => (hasReceipt(all, r.postId, r.surface) ? all : [...all, r]), base);

// Outside MainLayout on purpose: it remounts between the two '/' route blocks, and this state must not.
export const useAnnouncementStore = create<AnnouncementStore>()((set, get) => ({
  userId: null,
  status: LOAD_STATUS.idle,
  now: Date.now(),
  signedUpAt: null,
  receipts: [],
  releases: [],
  popup: null,
  panelOpen: false,
  linkedPostId: null,
  startLoad: (userId) =>
    set({ userId, status: LOAD_STATUS.loading, signedUpAt: null, receipts: [], releases: [], popup: null, panelOpen: false }),
  // A late reply for an earlier user is dropped; receipts marked while loading are kept.
  finishLoad: (userId, state) => {
    if (get().userId !== userId) return;
    set((s) =>
      state
        ? {
            status: LOAD_STATUS.ready,
            signedUpAt: state.signedUpAt,
            receipts: mergeReceipts(state.receipts, s.receipts),
            releases: state.releases
          }
        : { status: LOAD_STATUS.failed }
    );
  },
  addReceipts: (receipts) => set((s) => ({ receipts: mergeReceipts(s.receipts, receipts) })),
  tick: () => set({ now: Date.now() }),
  openPopup: (postIds, surface) => set({ popup: { postIds, surface } }),
  closePopup: () => set({ popup: null }),
  setPanelOpen: (panelOpen) => set({ panelOpen }),
  setLinkedPostId: (linkedPostId) => set({ linkedPostId })
}));
